import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AlarmSurvey from "./AlarmSurvey";
import AlarmVerificationForm from "./AlarmVerificationForm";
import documentCheckinService from "../../lib/DocumentCheckinService";
import API_CONFIG from "../../lib/ApiConfig";
import { Loader } from "lucide-react";
import CheckinsList from "./CheckinsList";
import confetti from "canvas-confetti";
import track from "../../lib/analytics/events";

// Fallback values if backend doesn't return reward amounts
const DEFAULT_SURVEY_REWARD = "$X";
const DEFAULT_DOCUMENT_REWARD = "$Y";
const DEFAULT_TOTAL_REWARD = "$Z";

const AlarmFlow = ({ onComplete, onBack }) => {
  const navigate = useNavigate();
  const { getIdToken, user } = useAuth();
  const configSlug = "alarm-monitoring-verification-v1";
  const [flowState, setFlowState] = useState("survey"); // 'survey' | 'transition' | 'verification' | 'thankYou'
  const [surveyData, setSurveyData] = useState(null);
  const [hasStartedVerification, setHasStartedVerification] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [existingCheckins, setExistingCheckins] = useState([]);
  const [loadingCheckins, setLoadingCheckins] = useState(false);
  const fireConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 80,
      origin: { x: 0.5, y: 0.3 },
      colors: ["#1C57A5", "#D4F6C9", "#CEC5FF", "#FFD700", "#A1CE90"],
    });
  };

  const fetchSurveyStatus = async (token) => {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/api/nationwide/survey-status`,
      {
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Survey status check failed: ${response.status}`);
    }
    return await response.json();
  };

  useEffect(() => {
    const fetchSurveyAndCheckins = async () => {
      try {
        setIsLoading(true);
        const token = await getIdToken();
        if (!token || !user) {
          return;
        }

        const status = await fetchSurveyStatus(token);

        if (status.surveyCompleted) {
          setSurveyData(status);
          if (status.requiresDocumentUpload) {
            setFlowState("transition");
            // Fetch existing checkins only when document upload is needed
            setLoadingCheckins(true);
            try {
              const checkins = await documentCheckinService.getCheckins(token);
              setExistingCheckins(checkins || []);
            } catch (error) {
              console.error("Error loading checkins:", error);
              setExistingCheckins([]);
            } finally {
              setLoadingCheckins(false);
            }
          } else {
            setFlowState("thankYou");
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading survey:", error);
        setIsLoading(false);
      }
    };

    if (user) {
      fetchSurveyAndCheckins();
    }
  }, [configSlug, user, getIdToken]);

  const handleSurveyComplete = async (data) => {
    // AlarmSurvey already handles server submission via SurveyService
    // We just receive the completion notification here
    setSurveyData(data);
    setIsLoading(true);

    try {
      const token = await getIdToken();
      // Ask the server whether this user needs document upload
      const status = await fetchSurveyStatus(token);
      setSurveyData(status);

      if (status.requiresDocumentUpload) {
        // Has alarm: proceed to document upload
        setLoadingCheckins(true);
        try {
          const checkins = await documentCheckinService.getCheckins(token);
          setExistingCheckins(checkins || []);
        } catch (error) {
          console.error("Error loading checkins:", error);
          setExistingCheckins([]);
        } finally {
          setLoadingCheckins(false);
        }
        setFlowState("transition");
        setIsLoading(false);
        fireConfetti();
      } else {
        // No alarm: show thank you
        track.alarmNoSystemAcknowledged({ source: "survey_complete" });
        setFlowState("thankYou");
        setIsLoading(false);
        fireConfetti();
      }
    } catch (error) {
      console.error("Error checking survey status:", error);
      // Safe fallback: show document upload (current behavior)
      setFlowState("transition");
      setIsLoading(false);
    }
  };

  // Scroll to top when flow state changes
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector("main")?.scrollTo(0, 0);
  }, [flowState]);

  const handleStartVerification = () => {
    track.alarmContinueToUploadClicked();
    setHasStartedVerification(true);
    setFlowState("verification");
  };

  const handleVerificationComplete = (formData) => {
    if (onComplete) {
      onComplete({
        survey: surveyData,
        verification: formData,
      });
    }
  };

  const handleBackToSurvey = () => {
    setFlowState("transition");
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-6">
        <div className="flex flex-col items-center justify-center p-8">
          <Loader className="w-8 h-8 animate-spin text-crimsonGlory mb-4" />
          <span className="text-gray-600">Loading survey...</span>
        </div>
      </div>
    );
  }

  // Render survey
  if (flowState === "survey") {
    return (
      <div className="w-full max-w-3xl mx-auto">
        <AlarmSurvey onComplete={handleSurveyComplete} onBack={onBack} />
      </div>
    );
  }

  // Render transition screen (survey complete, verification not started)
  if (flowState === "transition") {
    return (
      <div className="w-full max-w-3xl mx-auto">
        {/* Survey Summary Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    track.surveyResponsesModalOpened();
                    setShowSurveyModal(true);
                  }}
                  className="flex items-center gap-3 hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors w-full text-left"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-[#1C57A5]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Survey Complete
                    </h2>
                    <p className="text-sm text-gray-600">
                      Thank you for confirming your alarm details
                    </p>
                  </div>
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Survey Summary */}
            {surveyData && (surveyData.responses) && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Your Responses
                </h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Survey completed{" "}
                    {surveyData.dateCompleted
                      ? `on ${new Date(
                          surveyData.dateCompleted
                        ).toLocaleDateString()}`
                      : "successfully"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {surveyData.responses.length} question
                    {surveyData.responses.length !== 1 ? "s" : ""} answered
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reward Earned Banner */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
          <div>
            {existingCheckins.length > 0 ? (
              <>
                <p className="text-sm font-semibold text-green-800">You've earned {surveyData?.totalReward || DEFAULT_TOTAL_REWARD}!</p>
                <p className="text-xs text-green-700">Head to the Reward Center to claim your gift card</p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-green-800">You've earned {surveyData?.surveyReward || DEFAULT_SURVEY_REWARD}!</p>
                <p className="text-xs text-green-700">Upload your documentation to earn an additional {surveyData?.documentReward || DEFAULT_DOCUMENT_REWARD}</p>
              </>
            )}
          </div>
          {/* <button
            onClick={() => navigate("/rewards")}
            className="ml-auto text-sm font-medium text-[#1C57A5] hover:underline flex-shrink-0"
          >
            Reward Center &rarr;
          </button> */}
        </div>

        <CheckinsList checkins={existingCheckins} loading={loadingCheckins} />

        {/* Next Step Banner */}
        {(!isLoading && !loadingCheckins) && existingCheckins.length <= 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-[#1C57A5] rounded-lg p-6 mb-6">
            <div className="flex flex-col items-center text-center">
              {/* Icon */}
              <div className="w-12 h-12 bg-[#1C57A5] rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="w-full text-left">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 animate-jiggle">
                  Earn {surveyData?.documentReward || DEFAULT_DOCUMENT_REWARD} More - Upload Proof.
                </h3>

                <p className="text-gray-700 mb-4">
                  You're one step away from earning an additional {surveyData?.documentReward || DEFAULT_DOCUMENT_REWARD}!
                  Add a picture to verify your system and claim your full reward.
                </p>

                <ul className="space-y-2 mb-4">
                  {[
                    "Quick and easy upload process",
                    "Help us keep your information accurate",
                  ].map((text) => (
                    <li
                      key={text}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <svg
                        className="w-5 h-5 text-[#1C57A5]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {text}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleStartVerification}
                  className="bg-[#1C57A5] hover:bg-[#164a8f] text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2 mx-auto"
                >
                  Continue
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Go to Reward Center - shown after document upload */}
        {(!isLoading && !loadingCheckins) && existingCheckins.length > 0 && (
          <div className="text-center mt-6">
            <button
              onClick={() => {
                track.custom("nw_alarm_go_to_rewards_clicked", {
                  from: "transition",
                });
                navigate("/rewards");
              }}
              className="bg-[#1C57A5] hover:bg-[#164a8f] text-white font-medium px-8 py-3 rounded-lg transition-colors duration-200"
            >
              Go to Reward Center
            </button>
          </div>
        )}

        {/* Survey Review Modal */}
        {showSurveyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-xl font-semibold">Survey Responses</h2>
                <button
                  onClick={() => setShowSurveyModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="overflow-y-auto flex-1">
                <AlarmSurvey
                  onComplete={() => setShowSurveyModal(false)}
                  onBack={() => setShowSurveyModal(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render thank you screen (no alarm system)
  if (flowState === "thankYou") {
    return (
      <div className="w-full max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            {/* Success Icon */}
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Thank You for Your Response!
              </h2>
              <p className="text-gray-600 max-w-md">
                We appreciate you taking the time to share your information.
                Your responses have been recorded.
              </p>
            </div>

            {/* Reward Earned Banner */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-green-800">You've earned {surveyData?.surveyReward || DEFAULT_SURVEY_REWARD}!</p>
                <p className="text-xs text-green-700">Head to the Reward Center to claim your gift card</p>
              </div>
            </div>

            {/* View Responses Card */}
            <button
              onClick={() => {
                track.surveyResponsesModalOpened();
                setShowSurveyModal(true);
              }}
              className="w-full flex items-center gap-3 hover:bg-gray-50 rounded-lg p-4 border border-gray-200 transition-colors mb-6 text-left"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-[#1C57A5]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  View Your Responses
                </h3>
                <p className="text-xs text-gray-500">
                  Tap to review your survey answers
                </p>
              </div>
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Informational message */}
            {/* <div className="bg-blue-50 border-l-4 border-[#1C57A5] p-4 rounded-r-md mb-6">
              <p className="text-sm text-blue-800">
                If you install an alarm system in the future, you may be
                eligible for additional policy discounts. Contact your
                Nationwide agent to learn more.
              </p>
            </div> */}

            {/* Go to Reward Center Button */}
            <div className="text-center">
              <button
                onClick={() => {
                  track.custom("nw_alarm_go_to_rewards_clicked", {
                    from: "thank_you",
                  });
                  navigate("/rewards");
                }}
                className="bg-[#1C57A5] hover:bg-[#164a8f] text-white font-medium px-8 py-3 rounded-lg transition-colors duration-200"
              >
                Go to Reward Center
              </button>
            </div>
          </div>
        </div>

        {/* Survey Review Modal */}
        {showSurveyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-xl font-semibold">Survey Responses</h2>
                <button
                  onClick={() => setShowSurveyModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="overflow-y-auto flex-1">
                <AlarmSurvey
                  onComplete={() => setShowSurveyModal(false)}
                  onBack={() => setShowSurveyModal(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render verification form with breadcrumb
  if (flowState === "verification") {
    return (
      <div className="w-full max-w-3xl mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-6" aria-label="Progress">
          <ol className="flex items-center gap-2 text-sm">
            <li className="flex items-center gap-2">
              <button
                onClick={handleBackToSurvey}
                className="flex items-center gap-1 text-[#1C57A5] hover:text-[#164a8f] font-medium"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Survey Complete
              </button>
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </li>
            <li className="text-gray-900 font-medium">Document Verification</li>
          </ol>
        </nav>

        {/* Verification Form */}
        <AlarmVerificationForm
          surveyData={surveyData}
          onComplete={handleVerificationComplete}
          onBack={handleBackToSurvey}
        />
      </div>
    );
  }

  return null;
};

export default AlarmFlow;
